import { useEffect, useRef, useState } from 'react';

const useMobilePullToRefresh = ({ onRefreshDashboard }) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const rootRef = useRef(null);
  const pullStateRef = useRef({
    startY: 0,
    active: false,
    distance: 0,
  });

  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof window === 'undefined') {
      return undefined;
    }

    const threshold = 72;
    const maxPullDistance = 120;
    const getScrollTop = () => window.scrollY || document.documentElement.scrollTop || 0;

    const updatePullDistance = (nextDistance) => {
      pullStateRef.current.distance = nextDistance;
      setPullDistance(nextDistance);
    };

    const handleTouchStart = (event) => {
      if (isRefreshing || event.touches?.length !== 1) {
        return;
      }

      if (getScrollTop() > 0) {
        pullStateRef.current.active = false;
        updatePullDistance(0);
        return;
      }

      pullStateRef.current.startY = event.touches[0].clientY;
      pullStateRef.current.active = true;
      updatePullDistance(0);
    };

    const handleTouchMove = (event) => {
      if (!pullStateRef.current.active || event.touches?.length !== 1) {
        return;
      }

      if (getScrollTop() > 0) {
        pullStateRef.current.active = false;
        updatePullDistance(0);
        return;
      }

      const delta = event.touches[0].clientY - pullStateRef.current.startY;
      if (delta <= 0) {
        updatePullDistance(0);
        return;
      }

      const nextDistance = Math.min(maxPullDistance, delta * 0.65);
      updatePullDistance(nextDistance);

      if (delta > 6) {
        event.preventDefault();
      }
    };

    const handleTouchEnd = async () => {
      if (!pullStateRef.current.active) {
        updatePullDistance(0);
        return;
      }

      const shouldRefresh = pullStateRef.current.distance >= threshold;
      pullStateRef.current.active = false;
      updatePullDistance(0);

      if (shouldRefresh && typeof onRefreshDashboard === 'function') {
        setIsRefreshing(true);
        try {
          await onRefreshDashboard();
        } catch (error) {
          console.error('Error refreshing dashboard:', error);
        } finally {
          setIsRefreshing(false);
        }
      }
    };

    node.addEventListener('touchstart', handleTouchStart, { passive: true });
    node.addEventListener('touchmove', handleTouchMove, { passive: false });
    node.addEventListener('touchend', handleTouchEnd, { passive: true });
    node.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      node.removeEventListener('touchstart', handleTouchStart);
      node.removeEventListener('touchmove', handleTouchMove);
      node.removeEventListener('touchend', handleTouchEnd);
      node.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [onRefreshDashboard, isRefreshing]);

  return {
    rootRef,
    pullDistance,
    isRefreshing,
  };
};

export default useMobilePullToRefresh;
